import { useState, useMemo } from "react";
import {
  Paper,
  Typography,
  Chip,
  Box,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Collapse,
  Stack,
  Tooltip,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Divider,
} from "@mui/material";
import {
  Search,
  FileDownload,
  ExpandMore,
  ExpandLess,
  FilterAlt,
  FilterAltOff,
} from "@mui/icons-material";

// ─── Constants ────────────────────────────────────────────────
const SEVERITY_MAP = {
  critical: { color: "#D32F2F", bg: "#FDECEA" },
  high:     { color: "#F57C00", bg: "#FFF4E5" },
  warning:  { color: "#ED6C02", bg: "#FFF3E0" },
  info:     { color: "#0288D1", bg: "#E3F2FD" },
};

const STATUS_COLOR = {
  active:       "error",
  acknowledged: "warning",
  cleared:      "success",
};

// ─── Helpers ──────────────────────────────────────────────────
const fmt = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleString() : "—";

const calcDuration = (triggeredAt, clearedAt) => {
  if (!triggeredAt) return "—";
  const end = clearedAt ? new Date(clearedAt) : new Date();
  const ms = end - new Date(triggeredAt);
  if (ms < 0) return "—";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const exportCSV = (rows) => {
  const headers = [
    "metric_key", "severity", "status", "value_at_trigger",
    "triggered_at", "cleared_at", "acknowledged_at", "duration",
  ];
  const lines = rows.map((r) =>
    [
      r.metric_key,
      r.severity || "",
      r.status,
      r.value_at_trigger ?? "",
      r.triggered_at || "",
      r.cleared_at || "",
      r.acknowledged_at || "",
      calcDuration(r.triggered_at, r.cleared_at),
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(",")
  );
  const csv = [headers.join(","), ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `alert_history_${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Detail Panel ─────────────────────────────────────────────
function DetailPanel({ alert }) {
  const sev = SEVERITY_MAP[alert.severity] || {};

  const fields = [
    { label: "Alert ID",        value: alert.id,                  mono: true },
    { label: "Metric",          value: alert.metric_key,          bold: true },
    { label: "Value at Trigger",value: alert.value_at_trigger ?? "—", bold: true },
    { label: "Condition",       value: alert.operator && alert.threshold != null
        ? `${alert.operator} ${alert.threshold}` : "—" },
    { label: "Duration",        value: calcDuration(alert.triggered_at, alert.cleared_at), bold: true },
    { label: "Triggered At",    value: fmt(alert.triggered_at) },
    { label: "Cleared At",      value: fmt(alert.cleared_at) },
    { label: "Acknowledged At", value: fmt(alert.acknowledged_at) },
    ...(alert.acknowledged_by
      ? [{ label: "Acknowledged By", value: alert.acknowledged_by, mono: true }]
      : []),
  ];

  return (
    <Box
      sx={{
        px: 4,
        py: 2.5,
        backgroundColor: "#f8faff",
        borderBottom: "2px solid #e3eaf5",
        borderTop: "1px solid #e3eaf5",
      }}
    >
      <Stack direction="row" spacing={1} mb={2} alignItems="center">
        <Chip
          label={alert.severity?.toUpperCase() || "—"}
          size="small"
          sx={{
            fontWeight: 700,
            color: sev.color || "#999",
            backgroundColor: sev.bg || "#f5f5f5",
          }}
        />
        <Chip
          label={alert.status?.toUpperCase()}
          size="small"
          color={STATUS_COLOR[alert.status] || "default"}
        />
        <Typography variant="caption" color="text.secondary">
          Active for {calcDuration(alert.triggered_at, alert.cleared_at)}
        </Typography>
      </Stack>

      {/* Detail fields grid */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))"
        gap={2}
      >
        {fields.map(({ label, value, mono, bold }) => (
          <Box key={label}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              fontWeight={600}
              mb={0.3}
            >
              {label}
            </Typography>
            <Typography
              variant="body2"
              fontFamily={mono ? "monospace" : undefined}
              fontSize={mono ? 11 : undefined}
              fontWeight={bold ? 700 : 400}
              sx={{ wordBreak: "break-all" }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────
export default function HistoryTab({ alerts = [] }) {
  // Filter state
  const [search, setSearch]       = useState("");
  const [statusFilter, setStatus] = useState("all");
  const [severityFilter, setSev]  = useState("all");
  const [dateFrom, setDateFrom]   = useState("");
  const [dateTo, setDateTo]       = useState("");
  const [showFilters, setShowFilters] = useState(true);

  // Table state
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage]             = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy]       = useState("triggered_at");
  const [order, setOrder]           = useState("desc");

  // ── Filter logic ──────────────────────────────────────────
  const filtered = useMemo(() => {
    return alerts.filter((a) => {
      if (search && !a.metric_key?.toLowerCase().includes(search.toLowerCase()))
        return false;
      if (statusFilter !== "all" && a.status !== statusFilter)
        return false;
      if (severityFilter !== "all" && a.severity !== severityFilter)
        return false;
      if (dateFrom && new Date(a.triggered_at) < new Date(dateFrom))
        return false;
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        if (new Date(a.triggered_at) > to) return false;
      }
      return true;
    });
  }, [alerts, search, statusFilter, severityFilter, dateFrom, dateTo]);

  // ── Sort logic ────────────────────────────────────────────
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let aVal = a[orderBy] ?? "";
      let bVal = b[orderBy] ?? "";
      // Date comparison
      if (orderBy.endsWith("_at")) {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      if (aVal < bVal) return order === "asc" ? -1 : 1;
      if (aVal > bVal) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, orderBy, order]);

  // Paginated slice
  const paginated = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage]
  );

  const hasActiveFilters =
    search || statusFilter !== "all" || severityFilter !== "all" || dateFrom || dateTo;

  const clearFilters = () => {
    setSearch(""); setStatus("all"); setSev("all");
    setDateFrom(""); setDateTo("");
    setPage(0);
  };

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(field);
      setOrder("desc");
    }
    setPage(0);
  };

  const handleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // ── Column definitions (for sort headers) ────────────────
  const columns = [
    { id: "expand",           label: "",               sortable: false, width: 44 },
    { id: "metric_key",       label: "Metric",         sortable: true  },
    { id: "severity",         label: "Severity",       sortable: true,  width: 120 },
    { id: "status",           label: "Status",         sortable: true,  width: 130 },
    { id: "value_at_trigger", label: "Value",          sortable: true,  width: 90  },
    { id: "duration",         label: "Duration",       sortable: false, width: 100 },
    { id: "triggered_at",     label: "Triggered At",   sortable: true  },
    { id: "cleared_at",       label: "Cleared At",     sortable: true  },
  ];

  return (
    <Paper sx={{ borderRadius: 2 }}>
      {/* ── Header ── */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={3}
        pt={3}
        pb={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Alert History
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {filtered.length} of {alerts.length} alerts
            {hasActiveFilters && " (filtered)"}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
            <IconButton
              size="small"
              onClick={() => setShowFilters((v) => !v)}
              color={hasActiveFilters ? "primary" : "default"}
            >
              {hasActiveFilters ? (
                <FilterAlt fontSize="small" />
              ) : (
                <FilterAltOff fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Export filtered results as CSV">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownload fontSize="small" />}
                onClick={() => exportCSV(filtered)}
                disabled={filtered.length === 0}
              >
                Export CSV
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* ── Filter bar ── */}
      <Collapse in={showFilters}>
        <Box
          display="flex"
          gap={2}
          flexWrap="wrap"
          alignItems="center"
          mx={3}
          mb={2}
          p={2}
          sx={{ backgroundColor: "#f5f7ff", borderRadius: 1 }}
        >
          <TextField
            size="small"
            placeholder="Search metric..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ minWidth: 180 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            select size="small" label="Status"
            value={statusFilter}
            onChange={(e) => { setStatus(e.target.value); setPage(0); }}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="acknowledged">Acknowledged</MenuItem>
            <MenuItem value="cleared">Cleared</MenuItem>
          </TextField>

          <TextField
            select size="small" label="Severity"
            value={severityFilter}
            onChange={(e) => { setSev(e.target.value); setPage(0); }}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="all">All Severity</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="warning">Warning</MenuItem>
            <MenuItem value="info">Info</MenuItem>
          </TextField>

          <TextField
            size="small" label="From" type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <TextField
            size="small" label="To" type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          {hasActiveFilters && (
            <Button size="small" color="error" variant="text" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
        </Box>
      </Collapse>

      <Divider />

      {/* ── Table ── */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#fafafa" }}>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  width={col.width}
                  sx={{ fontWeight: 700, fontSize: 12, py: 1.5 }}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : "asc"}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                  <Typography color="text.disabled" variant="body2">
                    {hasActiveFilters
                      ? "No alerts match the current filters."
                      : "No alert history yet."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((alert) => (
                <>
                  {/* ── Main row ── */}
                  <TableRow
                    key={alert.id}
                    hover
                    onClick={() => handleExpand(alert.id)}
                    sx={{
                      cursor: "pointer",
                      backgroundColor:
                        expandedId === alert.id ? "#f0f4ff" : undefined,
                      "& td": { borderBottom: expandedId === alert.id ? "none" : undefined },
                    }}
                  >
                    {/* Expand toggle */}
                    <TableCell padding="checkbox">
                      <IconButton size="small" tabIndex={-1}>
                        {expandedId === alert.id ? (
                          <ExpandLess fontSize="small" sx={{ color: "#1976d2" }} />
                        ) : (
                          <ExpandMore fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>

                    {/* Metric */}
                    <TableCell>
                      <Tooltip title={alert.metric_key} placement="top">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          noWrap
                          sx={{ maxWidth: 200 }}
                        >
                          {alert.metric_key}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Severity */}
                    <TableCell>
                      {alert.severity ? (
                        <Chip
                          label={alert.severity.toUpperCase()}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            color: SEVERITY_MAP[alert.severity]?.color,
                            backgroundColor: SEVERITY_MAP[alert.severity]?.bg,
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Chip
                        label={alert.status?.toUpperCase()}
                        size="small"
                        color={STATUS_COLOR[alert.status] || "default"}
                      />
                    </TableCell>

                    {/* Value */}
                    <TableCell>
                      <Typography variant="body2">
                        {alert.value_at_trigger ?? "—"}
                      </Typography>
                    </TableCell>

                    {/* Duration */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {calcDuration(alert.triggered_at, alert.cleared_at)}
                      </Typography>
                    </TableCell>

                    {/* Triggered at */}
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {fmt(alert.triggered_at)}
                      </Typography>
                    </TableCell>

                    {/* Cleared at */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {fmt(alert.cleared_at)}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* ── Expandable detail row ── */}
                  <TableRow key={`${alert.id}-detail`}>
                    <TableCell
                      colSpan={columns.length}
                      sx={{ p: 0, border: "none" }}
                    >
                      <Collapse
                        in={expandedId === alert.id}
                        timeout="auto"
                        unmountOnExit
                      >
                        <DetailPanel alert={alert} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Pagination ── */}
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );
}
